import { useEffect, useMemo, useRef } from 'react'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import './RichTextEditor.css'

/**
 * Componente RichTextEditor
 * Editor de texto enriquecido para descripciones
 * @param {Object} props - Propiedades del componente
 * @param {string} props.value - Valor del editor
 * @param {Function} props.onChange - Función a ejecutar al cambiar el contenido
 * @param {string} props.placeholder - Texto placeholder
 */
function RichTextEditor({ value, onChange, placeholder = 'Escribe la descripción del producto...' }) {
  const editorElRef = useRef(null)
  const quillRef = useRef(null)
  const onChangeRef = useRef(onChange)
  const lastHtmlRef = useRef(value || '')

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  }), [])

  // Formatos permitidos
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link', 'image'
  ]

  useEffect(() => {
    const editorEl = editorElRef.current
    if (!editorEl) return
    if (quillRef.current) return

    const quill = new Quill(editorEl, {
      theme: 'snow',
      placeholder,
      modules
    })

    quillRef.current = quill

    const initialHtml = lastHtmlRef.current || ''
    if (initialHtml) {
      quill.clipboard.dangerouslyPasteHTML(initialHtml)
      lastHtmlRef.current = initialHtml
    }

    const handleTextChange = () => {
      const html = quill.root.innerHTML
      const normalized = html === '<p><br></p>' ? '' : html
      lastHtmlRef.current = normalized
      onChangeRef.current?.(normalized)
    }

    quill.on('text-change', handleTextChange)

    return () => {
      quill.off('text-change', handleTextChange)
      quillRef.current = null
      editorEl.innerHTML = ''
    }
  }, [modules, placeholder])

  useEffect(() => {
    const quill = quillRef.current
    if (!quill) return

    const nextHtml = value || ''
    if (nextHtml === lastHtmlRef.current) return

    const currentSelection = quill.getSelection()
    quill.clipboard.dangerouslyPasteHTML(nextHtml)
    lastHtmlRef.current = nextHtml
    if (currentSelection) {
      quill.setSelection(currentSelection.index, currentSelection.length)
    }
  }, [value])

  return (
    <div className="rich-text-editor">
      <div ref={editorElRef} data-formats={formats.join(',')} />
    </div>
  )
}

export default RichTextEditor

